import { Button } from '@avanta/shared/components/ui/Button';
import { Input } from '@avanta/shared/components/ui/Input';
import { LOCIZE_NS } from '@avanta/shared/constants/common';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSapaChatContext } from '../../context';
import { CSS_CLASSES, OPTION_VALUES } from '../../hooks/constants';
import { validateSapaContactValue } from '../../utils/validations';
import { type SingleQuestionProps } from './types';

const getSubmitButtonLabel = (
  isTextOnlyQuestion: boolean,
  isAnswered: boolean,
  t: (key: string) => string
): string => {
  if (!isTextOnlyQuestion) return t('Sapa.continue.cta');
  return isAnswered ? t('Sapa.states.sent') : t('Sapa.finish.cta');
};

const handleContactInputChange = (
  value: string,
  isTextOnlyQuestion: boolean,
  setTextInput: (v: string) => void,
  setErrorKey: (key: string | null) => void
): void => {
  setTextInput(value);
  if (isTextOnlyQuestion) {
    setErrorKey(validateSapaContactValue(value));
  }
};

export const SingleQuestion = ({ message }: SingleQuestionProps): React.ReactElement | null => {
  const { t } = useTranslation(LOCIZE_NS.SERVICES);
  const { answers, handleAnswer } = useSapaChatContext();
  const { options, questionId, hasTextInput, textInputLabel } = message;
  const [textInput, setTextInput] = useState<string>('');
  const [errorKey, setErrorKey] = useState<string | null>(null);
  const hasOptions = Array.isArray(options) && options.length > 0;
  const isTextOnlyQuestion = hasTextInput && !hasOptions;
  if (!questionId || (!hasOptions && !hasTextInput)) return null;
  const isAnswered = !!answers[questionId];
  const isSubmitButtonDisabled =
    isAnswered ||
    (!isTextOnlyQuestion && !textInput) ||
    (Boolean(isTextOnlyQuestion) && !!errorKey);

  const handleOptionClick = (optionValue: string): void => {
    if (!isAnswered) {
      handleAnswer(questionId, [optionValue]);
    }
  };
  const handleTextSubmit = (): void => {
    if (isTextOnlyQuestion) {
      const validationErrorKey = validateSapaContactValue(textInput);
      setErrorKey(validationErrorKey);
      if (validationErrorKey) return;
    }
    handleAnswer(questionId, [OPTION_VALUES.NONE], textInput);
  };
  return (
    <div className={CSS_CLASSES.SPACE_Y_2}>
      {hasOptions &&
        options?.map(option => (
          <Button
            key={option.id}
            variant={CSS_CLASSES.BUTTON_VARIANT_TERCIARY}
            className={CSS_CLASSES.BUTTON_FULL_WIDTH}
            disabled={isAnswered}
            onClick={() => handleOptionClick(option.value)}
          >
            {option.text}
          </Button>
        ))}
      {hasTextInput && !isAnswered && (
        <div className={CSS_CLASSES.TEXT_INPUT_CONTAINER}>
          <Input
            type="text"
            placeholder={textInputLabel}
            value={textInput}
            onChange={e =>
              handleContactInputChange(
                e.target.value,
                Boolean(isTextOnlyQuestion),
                setTextInput,
                setErrorKey
              )
            }
            className={CSS_CLASSES.INPUT_FULL_WIDTH}
          />
          {isTextOnlyQuestion && errorKey && (
            <p className="mt-1 text-xs text-red-500">{t(errorKey)}</p>
          )}
          <Button
            className={CSS_CLASSES.BUTTON_CONTINUE}
            disabled={isSubmitButtonDisabled}
            onClick={handleTextSubmit}
          >
            {getSubmitButtonLabel(Boolean(isTextOnlyQuestion), isAnswered, t)}
          </Button>
        </div>
      )}
    </div>
  );
};
